import { MouseEvent, useMemo } from 'react';
import { GridCellParams, GridRowSelectionModel, GRID_CHECKBOX_SELECTION_FIELD, MaterialDataGrid, MuiEvent } from '@enterprise/material-data-grid';
import styles from './combinedItemsGrid.module.scss';
import classNames from 'classnames';
import { CombinedItemsGridToolbar } from './toollbar';
import { combinedItemCustomColumnMenu } from './columnMenu';
import { CombinedItemsGridColumn, useCombinedItemGrid } from './hooks';
import { isEqual } from 'lodash';
import {
    CombinedItemBulkUpdateType,
    CombinedItemsSelectionModel,
    CombinedItemGridState,
    CombinedItemsPaginationModel,
    CombinedItemState,
} from '../../../../store';
import { CombinedItem } from '../../../../core/models';
import { CombinedItemDetailsGridColumn } from './hooks/useCombinedItemDetailPanel.hook';
import { GridRowParams } from '@mui/x-data-grid';
import { CombinedItemDetailPanel } from './detailsPanel';
import { useStores } from '../../../../hooks';

export interface ItemResultsProps {
    className?: string;
    items?: CombinedItem[];
    grouping?: CombinedItemsGridColumn[];
    selection?: CombinedItemsSelectionModel;
    pagination?: CombinedItemsPaginationModel;
    onSelectionChange?: (params: CombinedItemsSelectionModel) => void;
    onBulkActionChange?: (params: { type: CombinedItemBulkUpdateType }) => void;
    loading?: boolean;
    columnsState?: CombinedItemGridState;
    onColumnsStateChange?: (state: CombinedItemGridState) => void;
    detailsPanelState?: CombinedItemState;
    onDetailsPanelStateChange?: (state: CombinedItemState) => void;
}

export const CombinedItemsGrid = (props: ItemResultsProps) => {
    const {
        domain: { combinedItemStore },
    } = useStores();

    const { getRowId } = combinedItemStore;
    const {
        items = [],
        className,
        grouping,
        selection,
        pagination,
        onSelectionChange,
        onBulkActionChange,
        loading,
        columnsState: colState,
        onColumnsStateChange,
        detailsPanelState,
        onDetailsPanelStateChange,
    } = props;

    // const detailsGrouping = grouping?.reduce(
    //     (agg, item) => [
    //         ...agg,
    //         item === CombinedItemsGridColumn.Description ? CombinedItemDetailsGridColumn.Description : CombinedItemDetailsGridColumn.Id,
    //     ],
    //     [] as CombinedItemDetailsGridColumn[],
    // );

    const defaultDetailsPanelState: CombinedItemState = {
        grid: {
            // groupingModel: detailsGrouping,
            // visibilityModel: detailsGrouping?.reduce((agg, item) => {
            //     return { ...agg, [item]: false };
            // }, {} as Record<number | string, boolean | undefined>),
            sortModel: [{ field: CombinedItemDetailsGridColumn.Order, sort: 'asc' }],
        },
    };

    const defaultGridState: CombinedItemGridState = {
        groupingModel: grouping,
        visibilityModel: grouping?.reduce((agg, item) => {
            return { ...agg, [item]: false };
        }, {} as Record<number | string, boolean | undefined>),
        sortModel: [{ field: `__row_group_by_columns_group_${grouping?.[0]}__`, sort: 'asc' }],
        // pinned: {
        //     left: ['__row_group_by_columns_group_description__'], //grouping,
        // },
    };

    const isStateMutated = !isEqual(colState, defaultGridState) || !isEqual(detailsPanelState, defaultDetailsPanelState);

    const detailsState = detailsPanelState || defaultDetailsPanelState;
    const gridState = colState || defaultGridState;

    const {
        apiRef, //
        columns,
        groupColumn,
        getQuery,
        isTabletView,
        state,
    } = useCombinedItemGrid({
        columnsState: gridState,
        onColumnsStateChange,
    });

    const {
        pinnedColumns,
        onPinnedColumnsChange,
        onColumnWidthChange,
        columnVisibilityModel,
        onColumnVisibilityModelChange,
        sortModel,
        onSortModelChange,
        rowGroupingModel,
        onRowGroupingModelChange,
        onColumnOrderChange,
    } = state;

    const headerHeight = 68;
    const rowHeight = 52;

    const idToCombinedItems = useMemo(() => {
        return new Map<string, CombinedItem>(items.map((item) => [getRowId(item), item]));
    }, [items]);

    const selectionModel = useMemo(() => {
        const selectedIds = selection?.all ? idToCombinedItems.keys() : selection?.items?.keys();
        const ids = (selectedIds && [...selectedIds]) || [];

        return selection?.all && selection?.excluded ? ids.filter((id) => !selection?.excluded?.has(id)) : ids;
    }, [idToCombinedItems, selection]);

    const onSelectionModelChange = (selectionModel: GridRowSelectionModel) => {
        if (!onSelectionChange) {
            return;
        }

        const items = selectionModel.reduce((agg, id) => {
            const invoiceItem = idToCombinedItems.get(id as string);
            if (invoiceItem) {
                agg.set(id as string, invoiceItem);
            }

            return agg;
        }, new Map<string, CombinedItem>());
        onSelectionChange({ items });
    };

    const onSelectionAllChange = (params: { value: boolean }) => {
        onSelectionChange?.({ ...selection, all: params.value });
    };

    const onExitBulkMode = () => {
        onSelectionChange?.({ all: false, excluded: undefined, items: undefined });
    };

    const onCellClick = (params: GridCellParams, event: MuiEvent<MouseEvent>) => {
        if (params.field === GRID_CHECKBOX_SELECTION_FIELD) {
            return;
        }

        const query = getQuery(params);

        if (!query) {
            return;
        }

        event.preventDefault();
    };

    const getRowClassName = () => {
        return styles.row;
    };

    const toolbar = () => {
        const onColumnsReset = () => {
            onColumnsStateChange?.(defaultGridState);
            onDetailsPanelStateChange?.(defaultDetailsPanelState);
        };

        return (
            <CombinedItemsGridToolbar
                selection={selection}
                pagination={pagination}
                onSelectionAllChange={onSelectionAllChange}
                onExitBulkMode={onExitBulkMode}
                onAddToPractices={() => onBulkActionChange?.({ type: CombinedItemBulkUpdateType.AddToPractices })}
                isResetVisible={isStateMutated}
                onColumnsReset={onColumnsReset}
            />
        );
    };

    const getDetailPanelContent = (props: GridRowParams<CombinedItem>) => (
        <CombinedItemDetailPanel state={detailsState} onStateChange={onDetailsPanelStateChange} {...props} />
    );

    return (
        <MaterialDataGrid
            keepNonExistentRowsSelected
            className={classNames(styles.holder, className)}
            data-automation-id="select-all-data-grid"
            loading={loading}
            rows={items}
            columns={columns}
            apiRef={apiRef}
            rowHeight={rowHeight}
            headerHeight={headerHeight}
            // TODO: turned on on smaller screens - has impact on performance on small screens
            autoHeight={isTabletView}
            rowGroupingColumnMode="multiple"
            checkboxSelection
            getRowId={getRowId}
            getRowClassName={getRowClassName}
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={onSelectionModelChange}
            groupingColDef={groupColumn}
            onCellClick={onCellClick}
            disableRowSelectionOnClick={true}
            disableColumnFilter={false}
            disableColumnResize={false}
            pinnedColumns={pinnedColumns}
            onPinnedColumnsChange={onPinnedColumnsChange}
            onColumnWidthChange={onColumnWidthChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            rowGroupingModel={rowGroupingModel}
            onRowGroupingModelChange={(model) => onRowGroupingModelChange(model as CombinedItemsGridColumn[])}
            onColumnOrderChange={onColumnOrderChange}
            components={{
                Toolbar: toolbar,
                ColumnMenu: combinedItemCustomColumnMenu({ selection, onBulkActionChange }),
            }}
            classes={{
                groupingCriteriaCell: styles.groupingCriteriaCell,
                groupingCriteriaCellToggle: styles.groupingCriteriaCellToggle,
                columnHeaders: styles.columnHeaders,
                columnHeader: styles.columnHeader,
                pinnedColumnHeaders: styles.pinnedColumnHeaders,
                pinnedColumns: styles.pinnedColumns,
                detailPanel: styles.detailsHolder,
            }}
            getDetailPanelContent={getDetailPanelContent}
            getDetailPanelHeight={() => 'auto'}
        />
    );
};
